import type { AudioSource, AudioSourceHandlers } from '@soniox/client'
import { CaptureLostError } from '@/audio/CaptureLostError'
import { getServiceStrings } from '@/services/i18n'
import { createPcmAudioTap, type PcmAudioTap } from '@/audio/createPcmAudioTap'

/**
 * Soniox AudioSource that streams 16kHz mono 16-bit PCM audio from an existing
 * MediaStream. Used to share one mic capture between Soniox STT and local
 * archive recording.
 */
export class MediaStreamSource implements AudioSource {
  private audioContext: AudioContext | null = null
  private tap: PcmAudioTap | null = null
  private stream: MediaStream | null = null
  private handlers: AudioSourceHandlers | null = null
  /** The user's mute switch, remembered so `resume` cannot quietly undo it. */
  private micEnabled = true

  constructor(private readonly inputStream: MediaStream) {}

  async start(handlers: AudioSourceHandlers): Promise<void> {
    this.stream = this.inputStream
    this.handlers = handlers

    const audioTracks = this.stream.getAudioTracks()
    if (audioTracks.length === 0) {
      throw new Error(getServiceStrings().microphoneTrackMissingShort)
    }

    audioTracks.forEach((track) => {
      track.onended = () => {
        handlers.onError(new CaptureLostError(getServiceStrings().microphoneAccessEnded))
      }
    })

    this.audioContext = new AudioContext({ sampleRate: 16000 })
    this.tap = await createPcmAudioTap(this.audioContext, this.stream, {
      onData: handlers.onData,
      onError: () => handlers.onError(new Error(getServiceStrings().mediaRecorderErrorMic)),
    })
  }

  stop(): void {
    this.tap?.dispose()
    this.tap = null
    void this.audioContext?.close().catch(() => {
      /* ignore close errors */
    })
    this.audioContext = null
    this.stream = null
    this.handlers = null
  }

  /**
   * Mutes the microphone audio tracks. The PCM tap keeps running so resuming
   * is instant (no AudioContext/worklet rebuild); the socket is gated
   * separately in `useProxySttRecording` — a paused meeting must send NO
   * audio, because Soniox bills silent PCM exactly like speech.
   */
  pause(): void {
    this.setTracksEnabled(false)
  }

  /**
   * Restores the tracks to what the user's mute switch asks for, rather than
   * simply switching them all on. Resuming used to enable every track
   * unconditionally, so a meeting resumed while muted came back with a live
   * microphone under a button still reading "Unmute" (BRAI-403).
   */
  resume(): void {
    this.setTracksEnabled(this.micEnabled)
  }

  /** Mirrors the user's mute switch onto the capture — now, and after a resume. */
  setMicrophoneEnabled(enabled: boolean): void {
    this.micEnabled = enabled
    this.setTracksEnabled(enabled)
  }

  private setTracksEnabled(enabled: boolean): void {
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled
    })
  }

  /** Recreates the PCM tap on the same stream/context without releasing the capture device. */
  restart(nextHandlers?: AudioSourceHandlers): void {
    // Handlers are replaceable because a reconnect after a network outage taps
    // the SAME capture for a BRAND NEW socket: the stored handlers close over
    // the dead one, so reusing them would pipe PCM into a closed WebSocket.
    if (nextHandlers) this.handlers = nextHandlers
    if (!this.stream || !this.audioContext || !this.handlers) return
    const { stream, audioContext, handlers } = this
    this.tap?.dispose()
    this.tap = null
    void createPcmAudioTap(audioContext, stream, {
      onData: handlers.onData,
      onError: () => handlers.onError(new Error(getServiceStrings().mediaRecorderErrorMic)),
    })
      .then((tap) => {
        this.tap = tap
      })
      .catch(() => {
        /* ignore restart errors */
      })
  }
}
